<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1000px"
    persistent
    scrollable
    @keydown.esc="close"
  >
    <v-card>
      <v-card-title>
        <v-icon left>mdi-credit-card-outline</v-icon>
        <span>{{ $t('payment-method-ui.payment-method-details') }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :aria-label="$t('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-tabs v-model="activeTab" v-if="paymentMethod">
          <v-tab>
            <v-icon left small>mdi-information-outline</v-icon>
            {{ $t('information') }}
          </v-tab>
          <v-tab v-if="hasMandate">
            <v-icon left small>mdi-file-document-outline</v-icon>
            {{ $t('payment-method-ui.mandate') }}
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-code-tags</v-icon>
            {{ $t('payment-method-ui.technical-info') }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" v-if="paymentMethod">
          <!-- Tab 1: General Information -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <!-- Type and Status -->
                <div class="mb-4">
                  <div class="text-overline text--secondary mb-1">{{ $t('type') }} & {{ $t('status') }}</div>
                  <div class="d-flex align-center flex-wrap">
                    <v-chip small class="mr-2">{{ typeLabel(paymentMethod.type) }}</v-chip>
                    <v-chip small :color="statusColor(paymentMethod.status)" dark class="mr-2">
                      {{ $t('payment-method-ui.status.' + paymentMethod.status) || paymentMethod.status }}
                    </v-chip>
                    <v-chip v-if="paymentMethod.validated" small color="green" outlined>
                      <v-icon small left>mdi-check-circle</v-icon>
                      {{ $t('validated') }}
                    </v-chip>
                    <v-chip v-else-if="paymentMethod.validated === false" small color="orange" outlined>
                      <v-icon small left>mdi-clock-outline</v-icon>
                      {{ $t('pending-validation') }}
                    </v-chip>
                  </div>
                </div>

                <!-- Action Required Alert -->
            <v-alert v-if="requiresAction" type="warning" outlined dense class="mb-4">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <strong>{{ $t('action-required') }}</strong>
                  <div class="text-caption">{{ $t('payment-method-ui.authentication-required') }}</div>
                </div>
                <v-btn
                  v-if="nextActionUrl"
                  color="warning"
                  small
                  :href="nextActionUrl"
                  target="_blank"
                  class="ml-3"
                >
                  <v-icon small left>mdi-open-in-new</v-icon>
                  {{ $t('continue') }}
                </v-btn>
              </div>
            </v-alert>

          <!-- Summary -->
          <div class="mb-4" v-if="paymentMethod.summary || paymentMethod.pmSummary">
            <div class="text-overline text--secondary mb-1">{{ $t('summary') }}</div>
            <div class="text-body-1">{{ paymentMethod.summary || paymentMethod.pmSummary }}</div>
          </div>

            <!-- Generated From (for SEPA from Bancontact) -->
            <v-alert v-if="paymentMethod.generatedFrom === 'bancontact'" type="info" outlined dense class="mb-4">
              <div class="d-flex align-center">
                <v-icon color="blue" class="mr-2">mdi-bank-transfer</v-icon>
                <div>
                  <strong>{{ $t('payment-method-ui.generated-from-bancontact') }}</strong>
                  <div class="text-caption">{{ $t('payment-method-ui.generated-from-bancontact-info') }}</div>
                </div>
              </div>
            </v-alert>

            <!-- Card Details (for card type) -->
            <div class="mb-4" v-if="cardDetails">
              <div class="text-subtitle-2 mb-2">{{ $t('payment-method-ui.card-details') }}</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-if="cardDetails.brand">
                      <td><strong>{{ $t('brand') }}:</strong></td>
                      <td><v-chip x-small>{{ cardDetails.brand.toUpperCase() }}</v-chip></td>
                    </tr>
                    <tr v-if="cardDetails.last4">
                      <td><strong>{{ $t('last-4-digits') }}:</strong></td>
                      <td>•••• {{ cardDetails.last4 }}</td>
                    </tr>
                    <tr v-if="cardDetails.expMonth && cardDetails.expYear">
                      <td><strong>{{ $t('expiration') }}:</strong></td>
                      <td>{{ String(cardDetails.expMonth).padStart(2, '0') }}/{{ cardDetails.expYear }}</td>
                    </tr>
                    <tr v-if="cardDetails.country">
                      <td><strong>{{ $t('country') }}:</strong></td>
                      <td>
                        <v-chip x-small>{{ cardDetails.country }}</v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- SEPA Details (for SEPA type) -->
            <div class="mb-4" v-if="sepaDetails">
              <div class="text-subtitle-2 mb-2">{{ $t('payment-method-ui.sepa-details') }}</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-if="sepaDetails.ibanLast4">
                      <td><strong>IBAN:</strong></td>
                      <td>•••• •••• •••• {{ sepaDetails.ibanLast4 }}</td>
                    </tr>
                    <tr v-if="sepaDetails.bankCode">
                      <td><strong>{{ $t('payment-method-ui.bank-code') }}:</strong></td>
                      <td>{{ sepaDetails.bankCode }}</td>
                    </tr>
                    <tr v-if="sepaDetails.country">
                      <td><strong>{{ $t('country') }}:</strong></td>
                      <td><v-chip x-small>{{ sepaDetails.country }}</v-chip></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Billing Information -->
            <div class="mb-4" v-if="billingName || billingEmail">
              <div class="text-subtitle-2 mb-2">{{ $t('payment-method-ui.billing-information') }}</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-if="billingName">
                      <td><strong>{{ $t('name') }}:</strong></td>
                      <td>{{ billingName }}</td>
                    </tr>
                    <tr v-if="billingEmail">
                      <td><strong>{{ $t('email') }}:</strong></td>
                      <td>{{ billingEmail }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Signatory Information (for SEPA mandates) -->
            <div class="mb-4" v-if="signatoryFirstName || signatoryLastName">
              <div class="text-subtitle-2 mb-2">{{ $t('payment-method-ui.signatory-info') }}</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-if="signatoryFirstName">
                      <td><strong>{{ $t('firstname') }}:</strong></td>
                      <td>{{ signatoryFirstName }}</td>
                    </tr>
                    <tr v-if="signatoryLastName">
                      <td><strong>{{ $t('lastname') }}:</strong></td>
                      <td>{{ signatoryLastName }}</td>
                    </tr>
                    <tr v-if="signatoryFirstName && signatoryLastName">
                      <td><strong>{{ $t('payment-method-ui.full-name') }}:</strong></td>
                      <td>{{ signatoryFirstName }} {{ signatoryLastName }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Owner Type -->
            <div class="mb-4" v-if="paymentMethod.ownerType">
              <div class="text-overline text--secondary mb-1">{{ $t('payment-method-ui.owner-type') }}</div>
              <v-chip x-small outlined>{{ paymentMethod.ownerType }}</v-chip>
            </div>

                <!-- Created Date -->
                <div class="mb-4">
                  <div class="text-overline text--secondary mb-1">{{ $t('created') }}</div>
                  <div class="text-body-1">{{ formatDate(paymentMethod.createdAt || paymentMethod.created) }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Mandate Information -->
          <v-tab-item v-if="hasMandate">
            <v-card flat>
              <v-card-text>
                <!-- SEPA via Bancontact - Pas de signature ZMS nécessaire -->
                <v-alert v-if="paymentMethod.generatedFrom === 'bancontact'" type="info" outlined dense class="mb-4">
                  <div class="d-flex align-center">
                    <v-icon color="blue" class="mr-2">mdi-check-circle</v-icon>
                    <div>
                      <strong>{{ $t('payment-method-ui.mandate-validated-bancontact') }}</strong>
                      <div class="text-caption">{{ $t('payment-method-ui.mandate-validated-bancontact-info') }}</div>
                    </div>
                  </div>
                </v-alert>

                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="paymentMethod.mandate.reference">
                        <td><strong>RUM:</strong></td>
                        <td>{{ paymentMethod.mandate.reference }}</td>
                      </tr>
                      <tr v-if="paymentMethod.mandate.id">
                        <td><strong>{{ $t('payment-method-ui.mandate') }} ID:</strong></td>
                        <td class="text-truncate" style="max-width: 400px;">{{ paymentMethod.mandate.id }}</td>
                      </tr>
                      <tr v-if="paymentMethod.mandate.status">
                        <td><strong>{{ $t('status') }}:</strong></td>
                        <td>
                          <v-chip x-small :color="mandateStatusColor(paymentMethod.mandate.status)">
                            {{ paymentMethod.mandate.status }}
                          </v-chip>
                        </td>
                      </tr>
                      <tr v-if="paymentMethod.mandate.acceptedAtUtc">
                        <td><strong>{{ $t('payment-method-ui.accepted-at') }}:</strong></td>
                        <td>{{ formatDate(paymentMethod.mandate.acceptedAtUtc) }}</td>
                      </tr>
                      <tr v-if="icsNumber">
                        <td><strong>ICS:</strong></td>
                        <td>{{ icsNumber }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Mandate Action Buttons -->
                <div class="d-flex flex-wrap gap-2 mt-4">
                  <v-btn
                    v-if="paymentMethod.mandate.url"
                    color="primary"
                    outlined
                    small
                    @click="viewStripeMandate"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ $t('payment-method-ui.view-stripe-mandate') }}
                  </v-btn>

                  <v-btn
                    v-if="paymentMethod.mandate.id && paymentMethod.generatedFrom !== 'bancontact'"
                    color="primary"
                    outlined
                    small
                    :loading="loadingSignedMandate"
                    @click="viewSignedMandate"
                  >
                    <v-icon left small>mdi-file-document-outline</v-icon>
                    {{ $t('payment-method-ui.view-zeborne-mandate') }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Technical Information -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="paymentMethod.id">
                        <td><strong>{{ $t('payment-method-ui.database-id') }}:</strong></td>
                        <td>
                          <span class="text-caption text--secondary">{{ paymentMethod.id }}</span>
                        </td>
                      </tr>
                      <tr v-if="setupIntentId">
                        <td><strong>Setup Intent ID:</strong></td>
                        <td>
                          <span class="text-caption text--secondary">{{ setupIntentId }}</span>
                        </td>
                      </tr>
                      <tr v-if="stripePaymentMethodId">
                        <td><strong>Stripe Payment Method ID:</strong></td>
                        <td>
                          <span class="text-caption text--secondary">{{ stripePaymentMethodId }}</span>
                        </td>
                      </tr>
                      <tr v-if="paymentMethod.createdAt || paymentMethod.created">
                        <td><strong>{{ $t('payment-method-ui.created-at') }}:</strong></td>
                        <td>
                          <span class="text-caption">{{ formatDate(paymentMethod.createdAt || paymentMethod.created) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Stripe Mandate Dialog (with iframe) -->
    <v-dialog v-model="stripeMandateDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-pdf-box</v-icon>
          <span>{{ $t('payment-method-ui.view-stripe-mandate') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeStripeMandateDialog" :aria-label="$t('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <iframe
            v-if="stripeMandateUrl"
            :src="stripeMandateUrl"
            style="width: 100%; height: 600px; border: none;"
            frameborder="0"
          ></iframe>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small text @click="closeStripeMandateDialog">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ZEborne Signed Mandate Dialog -->
    <v-dialog v-model="signedMandateDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-document-outline</v-icon>
          <span>{{ $t('payment-method-ui.view-zeborne-mandate') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeSignedMandateDialog" :aria-label="$t('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-alert v-if="signedMandateError" type="error" dense outlined class="mb-4">
            {{ signedMandateError }}
          </v-alert>

          <v-progress-linear v-if="loadingSignedMandate" indeterminate color="primary" class="mb-4"/>

          <div v-if="signedMandateHtml && !loadingSignedMandate" class="signed-mandate-content pa-4" style="max-height: 600px; overflow-y: auto; border: 1px solid #ccc; border-radius: 4px;">
            <div v-html="signedMandateHtml"></div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small text @click="closeSignedMandateDialog">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import PaymentMethodRepository from '@repository/PaymentMethodRepository'

export default {
  name: 'PaymentMethodDetailDialog',

  components: {},

  props: {
    value: {
      type: Boolean,
      default: false
    },
    paymentMethod: {
      type: Object,
      default: null
    },
    organizationId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      activeTab: 0,
      loadingSignedMandate: false,
      signedMandateDialog: false,
      signedMandateHtml: null,
      signedMandateError: null,
      stripeMandateDialog: false,
      stripeMandateUrl: null
    }
  },

  computed: {
    requiresAction() {
      // Check if authentication is required based on PM status or setupIntent status
      if (this.paymentMethod?.validated === true) return false
      return this.paymentMethod?.status === 'requires_action' ||
             this.paymentMethod?.setupIntentStatus === 'requires_action' ||
             this.paymentMethod?.setupIntent?.status === 'requires_action'
    },

    nextActionUrl() {
      return this.paymentMethod?.setupIntent?.nextAction || null
    },

    cardDetails() {
      // Return card details if this is a card payment method
      if (this.paymentMethod?.type === 'card' && this.paymentMethod?.card) {
        return this.paymentMethod.card
      }
      // Also check in details.data
      if (this.paymentMethod?.type === 'card' && this.paymentMethod?.details?.data) {
        return this.paymentMethod.details.data
      }
      return null
    },

    billingName() {
      return this.paymentMethod?.details?.billing?.name ||
             this.paymentMethod?.billing?.name ||
             this.paymentMethod?.card?.name ||
             ''
    },

    billingEmail() {
      return this.paymentMethod?.details?.billing?.email ||
             this.paymentMethod?.billing?.email ||
             null
    },

    signatoryFirstName() {
      return this.paymentMethod?.mandate?.signatoryFirstName ||
             this.paymentMethod?.signatoryFirstName ||
             null
    },

    signatoryLastName() {
      return this.paymentMethod?.mandate?.signatoryLastName ||
             this.paymentMethod?.signatoryLastName ||
             null
    },

    sepaDetails() {
      // Return SEPA details if this is a SEPA payment method
      if (this.paymentMethod?.type === 'sepa_debit' && this.paymentMethod?.details?.data) {
        return this.paymentMethod.details.data
      }
      return null
    },

    setupIntentId() {
      return this.paymentMethod?.setupIntent?.id ||
             this.paymentMethod?.setupIntentId ||
             null
    },

    stripePaymentMethodId() {
      return this.paymentMethod?.stripePaymentMethodId ||
             this.paymentMethod?.paymentMethod ||
             null
    },

    icsNumber() {
      // You may need to fetch this from your backend or organization settings
      // This is a placeholder
      return this.paymentMethod?.mandate?.creditorId || 'BE18ZZ00000000000' // Example ICS
    },

    hasMandate() {
      // Check if payment method has a valid mandate (for SEPA)
      return this.paymentMethod?.mandate &&
             (this.paymentMethod.mandate.url ||
              this.paymentMethod.mandate.reference ||
              this.paymentMethod.mandate.id)
    }
  },

  methods: {
    close() {
      this.$emit('input', false)
    },

    viewStripeMandate() {
      if (!this.paymentMethod?.mandate?.url) return
      this.stripeMandateUrl = this.paymentMethod.mandate.url
      this.stripeMandateDialog = true
    },

    closeStripeMandateDialog() {
      this.stripeMandateDialog = false
      this.stripeMandateUrl = null
    },

    async viewSignedMandate() {
      const paymentMethodId = this.paymentMethod?.id
      if (!paymentMethodId) return

      // Open dialog and start loading
      this.signedMandateDialog = true
      this.loadingSignedMandate = true
      this.signedMandateError = null
      this.signedMandateHtml = null

      try {
        const locale = this.$i18n?.locale || 'fr'
        const result = await PaymentMethodRepository.getSignedMandateView(
          paymentMethodId,
          {
            organizationId: this.organizationId,
            locale
          }
        )

        // Display the HTML in the dialog
        if (result.renderedHtml || result.html) {
          this.signedMandateHtml = result.renderedHtml || result.html
        } else if (result.url) {
          // If the backend returns a URL instead, open in new window
          window.open(result.url, '_blank')
          this.signedMandateDialog = false
        } else {
          throw new Error('No HTML or URL returned from signed mandate API')
        }
      } catch (err) {
        this.signedMandateError = err?.response?.data?.message || err?.message || 'Error loading signed mandate'
      } finally {
        this.loadingSignedMandate = false
      }
    },

    closeSignedMandateDialog() {
      this.signedMandateDialog = false
      this.signedMandateHtml = null
      this.signedMandateError = null
    },

    typeLabel(type) {
      const labels = {
        card: this.$t('payment-method.type.card') || 'Card',
        sepa_debit: 'SEPA',
        bancontact: 'Bancontact',
        apple_pay: 'Apple Pay',
        google_pay: 'Google Pay',
        deferred: this.$t('payment-method.type.deferred') || 'Deferred'
      }
      return labels[type] || type || ''
    },

    statusColor(status) {
      const colors = {
        succeeded: 'green',
        processing: 'blue',
        requires_action: 'orange',
        requires_payment_method: 'orange',
        canceled: 'grey',
        failed: 'red'
      }
      return colors[status] || 'grey'
    },

    formatDate(val) {
      if (!val) return ''
      try {
        return new Date(val).toLocaleString()
      } catch (e) {
        return String(val)
      }
    },

    mandateStatusColor(status) {
      const colors = {
        active: 'green',
        inactive: 'grey',
        pending: 'orange'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
/* Dialog specific styles */
</style>
